import { useState, useEffect } from 'react';
import { TreeNode } from './TreeNode';

export interface UseTreeExpansionOptions {
  expandedNodes?: Set<string>;
  onExpandedNodesChange?: (nodes: Set<string>) => void;
  onLoadNodeChildren?: (nodeId: string, nodeType: 'year' | 'topic') => void;
  loadedNodeIds: Set<string>;
  loadingNodeIds: Set<string>;
  canLoadChildren: boolean;
}

export function useTreeExpansion({
  expandedNodes,
  onExpandedNodesChange,
  onLoadNodeChildren,
  loadedNodeIds,
  loadingNodeIds,
  canLoadChildren
}: UseTreeExpansionOptions) {
  const [nodesToAutoSelect, setNodesToAutoSelect] = useState<Set<string>>(new Set());

  const toggleNode = (nodeId: string, nodeType: 'subject' | 'keystage' | 'year' | 'topic' | 'topicGroup') => {
    if (!expandedNodes || !onExpandedNodesChange) {
      return;
    }
    
    const newExpanded = new Set(expandedNodes);
    const isCurrentlyExpanded = newExpanded.has(nodeId);
    
    if (isCurrentlyExpanded) {
      newExpanded.delete(nodeId);
      onExpandedNodesChange(newExpanded);
    } else {
      // Load once per year: expanding a topic only shows data already returned with the year.
      if (nodeType === 'year' && onLoadNodeChildren && canLoadChildren) {
        if (!loadedNodeIds.has(nodeId) && !loadingNodeIds.has(nodeId)) {
          onLoadNodeChildren(nodeId, nodeType);
        }
      }
      
      newExpanded.add(nodeId);
      onExpandedNodesChange(newExpanded);
    }
  };

  const handleCollapseAll = () => {
    if (onExpandedNodesChange) {
      onExpandedNodesChange(new Set());
    }
  };

  return {
    toggleNode,
    handleCollapseAll,
    nodesToAutoSelect,
    setNodesToAutoSelect
  };
}
